import { useTranslation } from "react-i18next";
import sarService from "../../services/sarService";
import { useState } from "react";
import Status from "../../enums/Status";
import { Alert, Button, ButtonGroup, Card, Col, Container, Row } from "react-bootstrap";
import { RejectModal } from "./RejectModal";
import { RequestAdditionalInformationModal } from "./RequestAdditionalInformationModal";

interface IApprovalButtonsProps {
    requestId: string;
    appealReason?: string;
}

export default function ApprovalButtons(props: IApprovalButtonsProps) {
    const { t } = useTranslation();

    const [status, setStatus] = useState<Status>(Status.NEUTRAL);
    const [showRejectionModal, setShowRejectionModal] = useState<boolean>(false);
    const [showAdditionalInfoModal, setShowAdditionalInfoModal] = useState<boolean>(false);

    const handleApproveClicked = async () => {
        setStatus(Status.SAVING);
        const success = await approve(props.requestId);

        if (success) {
            setStatus(Status.SAVE_SUCCESS);
        } else {
            setStatus(Status.SAVE_ERROR);
        }
    };

    const handleRejectClicked = () => setShowRejectionModal(true);

    const handleAdditionalInfoRequiredClicked = () => setShowAdditionalInfoModal(true);
    
    const handleRejectCloseClicked = () => setShowRejectionModal(false);
    
    const handleAdditionalInfoCloseClicked = () => setShowAdditionalInfoModal(false);
    
    const handleRejectSubmit = () => setTimeout(() => window.location.reload(), 1_000);

    const handleRequestAdditionalInfoSubmit = () => setTimeout(() => window.location.reload(), 1_000);

    const isDisabled = [Status.SAVING, Status.SAVE_SUCCESS].includes(status);
    const isModalVisible = showRejectionModal || showAdditionalInfoModal;

    return (
        <>
            <Card.Body className="pt-0">
                <Card.Text as="div">
                    <Container fluid>
                        <Row>
                            <Col>
                                {
                                    status !== Status.SAVE_SUCCESS &&
                                    <h2 className="h4">
                                        {t("general.actions")}
                                    </h2>
                                }
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                {
                                    !!props.appealReason &&
                                    <p>
                                        <strong>{t("dsar.rejection.appealNotes")}</strong>
                                        <br/>
                                        {props.appealReason}
                                    </p>
                                }
                                {
                                    status !== Status.SAVE_SUCCESS &&
                                    <ButtonGroup>
                                        <Button variant="success" disabled={isDisabled} onClick={handleApproveClicked}>
                                            {t("general.approve")}
                                        </Button>
                                        <Button variant="danger" disabled={isDisabled} onClick={handleRejectClicked}>
                                            {t("general.reject")}
                                        </Button>
                                        <Button variant="warning" disabled={isDisabled} onClick={handleAdditionalInfoRequiredClicked}>
                                            {t("dsar.requestInfo.button")}
                                        </Button>
                                    </ButtonGroup>
                                }
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col>
                                {
                                    status === Status.SAVING &&
                                    !isModalVisible &&
                                    <Alert variant="info">
                                        {t("general.pleaseWait")}
                                    </Alert>
                                }
                                {
                                    status === Status.SAVE_SUCCESS &&
                                    !isModalVisible &&
                                    <Alert variant="success">
                                        {t("dsar.success.approve")}
                                    </Alert>
                                }
                                {
                                    status === Status.SAVE_ERROR &&
                                    !isModalVisible &&
                                    <Alert variant="danger">
                                        {t("dsar.errors.approve")}
                                    </Alert>
                                }
                            </Col>
                        </Row>
                    </Container>
                </Card.Text>
            </Card.Body>

            <RejectModal
                show={showRejectionModal}
                requestId={props.requestId}
                onSubmit={handleRejectSubmit}
                onClose={handleRejectCloseClicked}
                />

            <RequestAdditionalInformationModal
                show={showAdditionalInfoModal}
                requestId={props.requestId}
                onSubmit={handleRequestAdditionalInfoSubmit}
                onClose={handleAdditionalInfoCloseClicked}
                />
        </>
    );
}

async function approve(id: string) {
    try {
        const result = await sarService.approveSAR(id);
        return result;
    } catch (e) {
        return false;
    }
}